import { cloneDeep, remove, uniqWith, uniqBy, isEqual, isEmpty, min, max } from 'lodash'
import * as PropTypes from 'prop-types'
import React from 'react'
import { Item, Menu } from 'react-contexify'
import produce from 'immer'
import 'react-contexify/dist/ReactContexify.min.css'
import { v4 as uuidV4 } from 'uuid'
import Popup from 'react-popup'

import { VISIT_STATUSES, QUAY_ROLES } from '../../../js/constants'
import {
  copyOrder,
  copyTerminalVisit,
  copyTerminalVisitAndReplaceOrders,
  findOrderByUuid,
  splitTerminalVisit
} from '../../../utils/general'
import {
  checkChainCargo,
  determineTotalDurationDelays,
  hasMultipleOrders,
  updateCargoEnd,
  determineShipLength,
  updateDefaultSlack,
  mergeVisits,
  mergeOrders,
  checkIfProductsAreEqualForOrders,
  getUniqueVisitsAndOrders,
  disableShiftOrders,
  disableMatchOrders
} from '../../../utils/terminalVisitUtils'
import './TerminalVisitContextMenu.scss'
import { insertTerminalVisitInBackend, setSelectedVisit, updateTerminalVisitInBackend } from '../../app/actions'
import { isVisitAtExternalQuay, shiftOrMatchOrdersWithTerminalVisit } from '../../../utils/terminalPlannerUtils'
import ExternalVisitForm from './externalVisitForm/ExternalVisitForm'
import { reportUserEvent, USER_EVENTS } from '../../../utils/analyticsUtils'

export const TERMINAL_VISIT_CONTEXT_MENU_ID = 'TERMINAL_VISIT_CONTEXT_MENU_ID'

const TerminalVisitContextMenu = props => {
  const { dispatch, config, authToken, customFetch, selectedVisits } = props
  const { visits, orders } = getUniqueVisitsAndOrders(selectedVisits)
  const selectedOrdersHaveSameProducts = checkIfProductsAreEqualForOrders(orders)

  function handleAddFollowUpTerminalVisit(args) {
    const { terminalVisit, order } = args.props

    const visitCopy = cloneDeep(terminalVisit)
    const orderCopy = findOrderByUuid(visitCopy, order.uuid)
    const followUpOrder = copyOrder(orderCopy)
    visitCopy.orders.push(followUpOrder)

    // remove the after slack time of the original order
    const totalDurationDelays = determineTotalDurationDelays(terminalVisit)
    const chainCargo = checkChainCargo(order, config, totalDurationDelays)
    if (chainCargo) {
      orderCopy.slackAfter = 0
      updateCargoEnd(orderCopy, config, totalDurationDelays)
    } else {
      orderCopy.cargoEnd -= orderCopy.slackAfter
      orderCopy.slackAfter = 0
    }

    // let the new order start right after the original one, remove slack prior
    followUpOrder.cargoStart = orderCopy.cargoEnd
    followUpOrder.slackPrior = 0
    if (chainCargo) {
      updateCargoEnd(followUpOrder, config, totalDurationDelays)
    } else {
      const duration = orderCopy.cargoEnd - orderCopy.cargoStart
      followUpOrder.cargoEnd = followUpOrder.cargoStart + duration
    }

    dispatch(updateTerminalVisitInBackend(visitCopy, authToken, customFetch))

    reportUserEvent(USER_EVENTS.CONTEXTMENU.ADD_FOLLOW_UP)
  }

  function handleAddParallelTerminalVisit(args) {
    const { terminalVisit, order } = args.props

    const newOrderUuid = uuidV4()

    const newOrder = cloneDeep(order)

    newOrder.uuid = newOrderUuid

    // The default parallelGroups is 2 for adding a parallel visit from the context menu
    newOrder.parallelGroup = terminalVisit.parallelGroups < 2 ? terminalVisit.parallelGroups : 1

    const updatedTerminalVisit = cloneDeep(terminalVisit)
    updatedTerminalVisit.orders.push(newOrder)

    const newParallelGroupsLength = uniqBy(updatedTerminalVisit.orders, 'parallelGroup').length
    if (updatedTerminalVisit.parallelGroups !== newParallelGroupsLength && updatedTerminalVisit.parallelGroups < 2) {
      updatedTerminalVisit.parallelGroups = newParallelGroupsLength
    }

    reportUserEvent(USER_EVENTS.CONTEXTMENU.ADD_PARALLEL)

    return props.dispatch(setSelectedVisit(updatedTerminalVisit, newOrderUuid))
  }

  function handleSeparateTerminalVisit(args) {
    const { terminalVisit, order } = args.props

    // remove the order from the existing terminal visit
    const updatedVisit = cloneDeep(terminalVisit)
    remove(updatedVisit.orders, o => o.uuid === order.uuid)

    // Check if visit is parallel group and make normal visit if necessary
    if (updatedVisit.parallelGroups > 1) {
      const uniqOrderParallelGroupsLength = uniqBy(updatedVisit.orders, 'parallelGroup').length

      if (uniqOrderParallelGroupsLength === 1) {
        updatedVisit.parallelGroups = 1
      }
    }

    dispatch(updateTerminalVisitInBackend(updatedVisit, authToken, customFetch))

    // create a new terminal visit with the separated order
    const separatedVisit = copyTerminalVisit(terminalVisit)
    separatedVisit.orders = [copyOrder(order)]
    separatedVisit.parallelGroups = 1
    separatedVisit.orders[0].parallelGroup = 0
    dispatch(insertTerminalVisitInBackend(separatedVisit, config, authToken, customFetch))

    reportUserEvent(USER_EVENTS.CONTEXTMENU.SEPARATE)
  }

  function handleSplitTerminalVisit(args) {
    const { terminalVisit } = args.props

    const result = splitTerminalVisit(terminalVisit)
    dispatch(updateTerminalVisitInBackend(result.current, authToken, customFetch))
    dispatch(insertTerminalVisitInBackend(result.copy, config, authToken, customFetch))

    reportUserEvent(USER_EVENTS.CONTEXTMENU.SPLIT)
  }

  function handleCopyTerminalVisit(args) {
    const { terminalVisit, order } = args.props

    const copy = copyTerminalVisitAndReplaceOrders(terminalVisit, order, config)
    if (copy.parallelGroups > 1) {
      copy.orders[0].parallelGroup = 0
      copy.parallelGroups = 1
    }
    dispatch(insertTerminalVisitInBackend(copy, config, authToken, customFetch))

    reportUserEvent(USER_EVENTS.CONTEXTMENU.COPY)
  }

  function createBargeVisitsPopup(args) {
    const { terminalVisit } = args.props
    Popup.create({
      title: 'ExternalVisitForm',
      className: 'small-picker',
      content: (
        <ExternalVisitForm
          terminalVisit={terminalVisit}
          createExternalVisitBargeVisits={createExternalVisitBargeVisits}
        />
      )
    })
  }

  async function createExternalVisitBargeVisits(terminalVisit, numberOfBargeVisits) {
    // close the external visit form popup
    Popup.close()
    const defaultOffset = 3600000 * 3 // 6 hours default offset for the barge visits
    const visitCopy = cloneDeep(terminalVisit)
    const firstOrder = visitCopy.orders[0]
    // used to determine the next visits starting point with. By default uses the external visit end + a default offset
    let nextOrderStart = firstOrder.cargoStart + defaultOffset
    // the quay to put the visit on, takes the first quay marked as 'NORMAL' for now.
    const defaultQuay = props.quays.filter(quay => quay.role === QUAY_ROLES.normal)[0]
    // create new bargeVisits based on the selected terminalVisit.
    for (let index = 0; index < numberOfBargeVisits; index++) {
      const newBargeVisit = cloneDeep(visitCopy)
      const newBargeOrder = cloneDeep(firstOrder)
      // reset and delete values from the visit
      delete newBargeVisit._id
      newBargeVisit.delays = []
      newBargeVisit.demurrages = []
      newBargeVisit.quayId = defaultQuay.name
      newBargeVisit.status = VISIT_STATUSES.planned
      newBargeVisit.dependsOnVisit = visitCopy._id
      newBargeVisit.ship = {
        type: 'barge',
        length: determineShipLength(newBargeOrder.product, config)
      }
      // create the new order with calculated times and strip it of some of its values
      newBargeOrder.cargoStart = nextOrderStart
      const lastOrder = index === numberOfBargeVisits - 1
      // Calculate the amount by splitting it over all orders, make sure the last order gets the remaining amount (1000 > 1: 333.333, 2: 333.333, 3: 333.334 )
      newBargeOrder.amount = lastOrder
        ? Math.round(
            (firstOrder.amount -
              (Math.round((firstOrder.amount / numberOfBargeVisits) * 10000) / 10000) * (numberOfBargeVisits - 1)) *
              10000
          ) / 10000
        : Math.round((firstOrder.amount / numberOfBargeVisits) * 10000) / 10000

      delete newBargeOrder.uuid
      delete newBargeOrder.slackPrior
      delete newBargeOrder.slackAfter
      const updatedOrder = updateCargoEnd(newBargeOrder, config, 0) // sets the cargoEnd taking the operation, amount and product
      const updatedOrderWithSlacktimes = updateDefaultSlack(updatedOrder, newBargeVisit.ship, config)
      nextOrderStart = updatedOrder.cargoEnd // set the attribute to use for the next visits start position
      // make sure the new barge visit has the newly created order attached to it
      newBargeVisit.orders = [updatedOrderWithSlacktimes]
      await dispatch(insertTerminalVisitInBackend(newBargeVisit, config, authToken, customFetch))
    }

    reportUserEvent(USER_EVENTS.CONTEXTMENU.CREATE_BARGE_VISITS)
  }

  function selectShipForContextAction(visits, orders, callbackFunction) {
    const allShips = visits.map(visit => {
      return visit.ship
    })

    const uniqueShips = uniqWith(allShips, isEqual)

    if (uniqueShips.length <= 1) {
      callbackFunction(visits, orders, uniqueShips[0])
      return
    }

    Popup.create({
      title: 'Ship Selection',
      className: 'small-picker',
      content: (
        <div className="merge-visits-ship-select-wrapper">
          <h3>Which ship do you want to create the visit for?</h3>

          {uniqueShips.map((ship, index) => {
            return (
              <div
                key={'ship-selector' + index}
                className="ship-select-box"
                onClick={() => {
                  callbackFunction(visits, orders, ship)
                  Popup.close()
                }}
              >
                <h4>{ship.name ? ship.name : 'unknown ship'}</h4>
                <div className="ship-property">
                  <span>Length</span> {ship.length ? ship.length + 'm' : '-'}
                </div>
                <div className="ship-property">
                  <span>Type</span> {ship.type ? ship.type : '-'}
                </div>
              </div>
            )
          })}
        </div>
      )
    })
  }

  async function combineOrdersIntoOneVisit(visits, orders, ship) {
    // make a single visit with multiple orders from the selected visits together
    // find all the visits to delete after the merge
    const visitsToDelete = visits.map(visit => {
      return visit._id
    })

    // merge the visits together. Later add all the orders to this merged visit
    const mergedVisit = mergeVisits(visits, ship)

    if (mergedVisit === null) {
      console.warn('we could not merge these visits correctly')
    }

    // add all the orders to the merged visits
    mergedVisit.orders = orders

    // save the new visit and delete the old.
    await dispatch(insertTerminalVisitInBackend(mergedVisit, config, authToken, customFetch))
    props.deleteMultipleTerminalVisits(visitsToDelete)
    props.clearSelectedVisits()

    reportUserEvent(USER_EVENTS.CONTEXTMENU.COMBINE)
  }

  async function mergeOrdersIntoOneVisit(visits, orders, ship) {
    // make a single visit, with all the selected orders merged into one order.

    // find all the visits to delete after the merge
    const visitsToDelete = visits.map(visit => {
      return visit._id
    })

    // sort all the orders by their cargoStart.
    const sortedOrders = orders.sort((orderA, orderB) => {
      if (orderA.cargoStart < orderB.cargoStart) {
        return -1
      }
      if (orderA.cargoStart > orderB.cargoStart) {
        return 1
      }
      return 0
    })

    // merge the visits together
    const mergedVisit = mergeVisits(visits, ship)
    // merge the orders together so we have 1 visit and 1 order.
    const mergedOrder = mergeOrders(sortedOrders, ship, config)

    if (mergedVisit === null || mergedOrder === null) {
      console.warn('we could not merge these visits correctly')
    }

    // add the merged order to the visits
    mergedVisit.orders = [mergedOrder]

    // save the new merged visit in the backend
    await dispatch(insertTerminalVisitInBackend(mergedVisit, config, authToken, customFetch))
    // delete the old visits / orders from the backend
    props.deleteMultipleTerminalVisits(visitsToDelete)
    props.clearSelectedVisits()

    reportUserEvent(USER_EVENTS.CONTEXTMENU.MERGE)
  }

  function mergeOrCombineOrders(actionType, visits, orders) {
    if (actionType === 'MERGE') {
      selectShipForContextAction(visits, orders, mergeOrdersIntoOneVisit)
    } else {
      selectShipForContextAction(visits, orders, combineOrdersIntoOneVisit)
    }
  }

  const handleShiftToAta = args => {
    const { terminalVisit } = args.props

    const plannedVisitStart = min(terminalVisit.orders.map(ord => ord.cargoStart))
    const diffTime = terminalVisit.ataTime - plannedVisitStart

    const visitWithUpdatedOrders = produce(terminalVisit, draftVisit => {
      draftVisit.orders.forEach(ord => {
        ord.cargoStart += diffTime
        ord.cargoEnd += diffTime
      })
      return draftVisit
    })

    const updatedVisit = shiftOrMatchOrdersWithTerminalVisit(/** @type {*} */ (visitWithUpdatedOrders), config)

    dispatch(updateTerminalVisitInBackend(updatedVisit, authToken, customFetch))
  }

  const handleMatchToAtaAtd = args => {
    const { terminalVisit } = args.props

    const plannedVisitStart = min(terminalVisit.orders.map(ord => ord.cargoStart))
    const plannedVisitEnd = max(terminalVisit.orders.map(ord => ord.cargoEnd))

    const visitWithUpdatedOrders = produce(terminalVisit, draftVisit => {
      if (draftVisit.orders.length === 1) {
        draftVisit.orders[0].cargoStart = terminalVisit.ataTime
        draftVisit.orders[0].cargoEnd = terminalVisit.atdTime
      } else {
        const scalingFactor = (terminalVisit.atdTime - terminalVisit.ataTime) / (plannedVisitEnd - plannedVisitStart)
        draftVisit.orders.forEach(ord => {
          ord.cargoStart = terminalVisit.ataTime + (ord.cargoStart - plannedVisitStart) * scalingFactor
          ord.cargoEnd = terminalVisit.ataTime + (ord.cargoEnd - plannedVisitStart) * scalingFactor
        })
      }
      return draftVisit
    })

    const updatedVisit = shiftOrMatchOrdersWithTerminalVisit(/** @type {*} */ (visitWithUpdatedOrders), config)

    dispatch(updateTerminalVisitInBackend(updatedVisit, authToken, customFetch))
  }

  return (
    <Menu id={TERMINAL_VISIT_CONTEXT_MENU_ID} className="terminal-visit-context-menu">
      <Item
        onClick={handleShiftToAta}
        disabled={(/** @type {*} */ args) => disableShiftOrders(args.props.terminalVisit)}
      >
        Shift order start to ATA
      </Item>

      <Item
        onClick={handleMatchToAtaAtd}
        disabled={(/** @type {*} */ args) => disableMatchOrders(args.props.terminalVisit)}
      >
        Match order times to ATA / ATD
      </Item>

      <Item
        onClick={handleAddFollowUpTerminalVisit}
        disabled={(/** @type {*} */ args) => args.props.terminalVisit.status === VISIT_STATUSES.sailed}
      >
        Add follow up order
      </Item>

      <Item
        onClick={handleAddParallelTerminalVisit}
        disabled={(/** @type {*} */ args) => args.props.terminalVisit.status === VISIT_STATUSES.sailed}
      >
        Add parallel order
      </Item>

      <Item
        onClick={handleSeparateTerminalVisit}
        disabled={(/** @type {*} */ args) => !hasMultipleOrders(args.props.terminalVisit)}
      >
        Ungroup order
      </Item>

      <Item
        onClick={() => {
          mergeOrCombineOrders('COMBINE', visits, orders)
        }}
        disabled={() => isEmpty(selectedVisits) || selectedVisits.length <= 1}
      >
        {/* Single visit > multiple orders */}
        Group Orders
      </Item>

      <Item
        onClick={() => {
          mergeOrCombineOrders('MERGE', visits, orders)
        }}
        disabled={() => !selectedOrdersHaveSameProducts || isEmpty(selectedVisits) || selectedVisits.length <= 1}
      >
        {/* Single visit > One Order */}
        Merge Orders
      </Item>

      <Item
        onClick={handleSplitTerminalVisit}
        disabled={(/** @type {*} */ args) => hasMultipleOrders(args.props.terminalVisit)}
      >
        Split order
      </Item>

      <Item
        onClick={createBargeVisitsPopup}
        disabled={(/** @type {*} */ args) => !isVisitAtExternalQuay(args.props.terminalVisit, props.quays)}
      >
        Create Barge Visits
      </Item>

      <Item onClick={handleCopyTerminalVisit}>Copy order</Item>
    </Menu>
  )
}

export default TerminalVisitContextMenu

TerminalVisitContextMenu.propTypes = {
  config: PropTypes.object.isRequired,
  authToken: PropTypes.string.isRequired,
  customFetch: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  quays: PropTypes.array.isRequired,
  selectedVisits: PropTypes.array.isRequired,
  deleteMultipleTerminalVisits: PropTypes.func.isRequired,
  clearSelectedVisits: PropTypes.func.isRequired
}
